import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  GripVertical,
  FileText,
  Check,
  Loader2,
  Lock,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTION_TYPES = [
  { id: 'multiple_choice', label: 'Multiple Choice' },
  { id: 'rating', label: 'Rating Scale (1-5)' },
  { id: 'open_ended', label: 'Open Ended Text' }
];

const GOVERNANCE_LEVELS = [
  { id: 'federal', label: 'Federal' },
  { id: 'state', label: 'State' },
  { id: 'local', label: 'Local (LGA)' }
];

const POLICY_CATEGORIES = [
  { id: 'economy', label: 'Economy & Finance' },
  { id: 'security', label: 'Security & Safety' },
  { id: 'education', label: 'Education' },
  { id: 'health', label: 'Healthcare' },
  { id: 'infrastructure', label: 'Infrastructure' },
  { id: 'youth', label: 'Youth Development' },
  { id: 'women', label: 'Women Affairs' },
  { id: 'jobs', label: 'Employment' }
];

export default function CreateSurvey() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accessInfo, setAccessInfo] = useState(null);
  const [email, setEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  
  const [survey, setSurvey] = useState({
    title: '',
    description: '',
    governance_level: 'federal',
    policy_category: 'infrastructure',
    questions: [
      { text: '', question_type: 'multiple_choice', required: true, options: ['', ''] }
    ]
  });

  useEffect(() => {
    // Check for stored email
    const storedEmail = localStorage.getItem('cv_paid_email');
    if (storedEmail) {
      setEmail(storedEmail);
      verifyAccess(storedEmail);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyAccess = async (emailToCheck) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/reports/access/${encodeURIComponent(emailToCheck)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.has_access) {
          if (data.package_type === 'single_report') {
            toast.error('Survey creation requires monthly or annual subscription');
            setAccessInfo(null);
          } else {
            setAccessInfo(data);
            setEmailVerified(true);
            localStorage.setItem('cv_paid_email', emailToCheck);
          }
        } else {
          setAccessInfo(null);
          toast.error('No active subscription found for this email');
        }
      }
    } catch (error) {
      console.error('Error verifying access:', error);
      toast.error('Error verifying subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      verifyAccess(email);
    } else {
      toast.error('Please enter a valid email');
    }
  };

  const addQuestion = () => {
    setSurvey({
      ...survey,
      questions: [
        ...survey.questions,
        { text: '', question_type: 'multiple_choice', required: true, options: ['', ''] }
      ]
    });
  };

  const removeQuestion = (index) => {
    if (survey.questions.length > 1) {
      const newQuestions = survey.questions.filter((_, i) => i !== index);
      setSurvey({ ...survey, questions: newQuestions });
    }
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...survey.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    
    // Reset options when changing question type
    if (field === 'question_type') {
      if (value === 'multiple_choice') {
        newQuestions[index].options = ['', ''];
      } else {
        newQuestions[index].options = [];
      }
    }
    
    setSurvey({ ...survey, questions: newQuestions });
  };

  const addOption = (qIndex) => {
    const newQuestions = [...survey.questions];
    newQuestions[qIndex].options = [...newQuestions[qIndex].options, ''];
    setSurvey({ ...survey, questions: newQuestions });
  };

  const updateOption = (qIndex, optIndex, value) => {
    const newQuestions = [...survey.questions];
    newQuestions[qIndex].options[optIndex] = value;
    setSurvey({ ...survey, questions: newQuestions });
  };

  const removeOption = (qIndex, optIndex) => {
    const newQuestions = [...survey.questions];
    if (newQuestions[qIndex].options.length > 2) {
      newQuestions[qIndex].options = newQuestions[qIndex].options.filter((_, i) => i !== optIndex);
      setSurvey({ ...survey, questions: newQuestions });
    }
  };

  const handleSubmit = async () => {
    // Validate
    if (!survey.title.trim()) {
      toast.error('Please enter a survey title');
      return;
    }
    
    if (survey.questions.some(q => !q.text.trim())) {
      toast.error('All questions must have text');
      return;
    }
    
    if (survey.questions.some(q => 
      q.question_type === 'multiple_choice' && 
      q.options.some(opt => !opt.trim())
    )) {
      toast.error('All options must have text');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        email: email,
        title: survey.title,
        description: survey.description,
        governance_level: survey.governance_level,
        policy_category: survey.policy_category,
        questions: survey.questions.map(q => ({
          text: q.text,
          question_type: q.question_type,
          required: q.required,
          options: q.question_type === 'multiple_choice' ? q.options.filter(o => o.trim()) : []
        }))
      };

      const response = await fetch(`${API}/paid/surveys/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Survey created successfully!');
        navigate(`/survey/${data.survey_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create survey');
      }
    } catch (error) {
      console.error('Error creating survey:', error);
      toast.error('Failed to create survey');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Email verification form
  if (!emailVerified) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <Link to="/">
                  <Button variant="ghost" size="sm">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Home
                  </Button>
                </Link>
                <span className="font-semibold">Create Survey</span>
              </div>
              <LanguageSelector />
            </div>
          </div>
        </header>

        <main className="max-w-md mx-auto px-4 py-16">
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-primary" />
              </div>
              <CardTitle>Subscriber Access</CardTitle>
              <CardDescription>
                Enter your subscription email to create custom surveys
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="mt-1"
                  />
                </div>
                <Button type="submit" className="w-full">
                  Verify Subscription
                </Button>
              </form>
              
              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <div className="flex gap-2 items-start">
                  <AlertCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium">Don't have a subscription?</p>
                    <p className="mt-1">
                      Monthly and annual subscribers can create unlimited custom surveys.{' '}
                      <Link to="/reports/purchase" className="text-primary hover:underline">
                        Get a subscription
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Home
                </Button>
              </Link>
              <span className="font-semibold">Create Survey</span>
              <Badge variant="secondary" className="ml-2">
                {accessInfo?.package_type === 'annual_subscription' ? 'Annual' : 'Monthly'} Subscriber
              </Badge>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">{email}</span>
              <LanguageSelector />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Survey Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Survey Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Survey Title *</Label>
              <Input
                value={survey.title}
                onChange={(e) => setSurvey({ ...survey, title: e.target.value })}
                placeholder="e.g., Community Infrastructure Assessment"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={survey.description}
                onChange={(e) => setSurvey({ ...survey, description: e.target.value })}
                placeholder="Brief description of what this survey is about..."
                className="mt-1"
                rows={3}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Governance Level</Label>
                <Select
                  value={survey.governance_level}
                  onValueChange={(value) => setSurvey({ ...survey, governance_level: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GOVERNANCE_LEVELS.map(level => (
                      <SelectItem key={level.id} value={level.id}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Policy Category</Label>
                <Select
                  value={survey.policy_category}
                  onValueChange={(value) => setSurvey({ ...survey, policy_category: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POLICY_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Questions */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Questions</h2>
            <Button onClick={addQuestion} variant="outline" size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Add Question
            </Button>
          </div>

          {survey.questions.map((question, qIndex) => (
            <Card key={qIndex}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="text-muted-foreground mt-2">
                    <GripVertical className="h-5 w-5" />
                  </div>
                  
                  <div className="flex-1 space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <Label>Question {qIndex + 1} *</Label>
                        <Input
                          value={question.text}
                          onChange={(e) => updateQuestion(qIndex, 'text', e.target.value)}
                          placeholder="Enter your question..."
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="w-48">
                        <Label>Type</Label>
                        <Select
                          value={question.question_type}
                          onValueChange={(value) => updateQuestion(qIndex, 'question_type', value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {QUESTION_TYPES.map(type => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {question.question_type === 'multiple_choice' && (
                      <div className="pl-4 border-l-2 border-muted space-y-2">
                        <Label className="text-sm text-muted-foreground">Options</Label>
                        {question.options.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-center gap-2">
                            <Input
                              value={option}
                              onChange={(e) => updateOption(qIndex, optIndex, e.target.value)}
                              placeholder={`Option ${optIndex + 1}`}
                              className="flex-1"
                            />
                            {question.options.length > 2 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeOption(qIndex, optIndex)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addOption(qIndex)}
                          className="text-primary"
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          Add Option
                        </Button>
                      </div>
                    )}

                    {question.question_type === 'rating' && (
                      <p className="text-sm text-muted-foreground pl-4">
                        Respondents will rate on a scale of 1-5 stars
                      </p>
                    )}

                    {question.question_type === 'open_ended' && (
                      <p className="text-sm text-muted-foreground pl-4">
                        Respondents will provide free-text answers
                      </p>
                    )}
                  </div>

                  {survey.questions.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeQuestion(qIndex)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Link to="/">
            <Button variant="outline">Cancel</Button>
          </Link>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                Create Survey
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}
